import React, { useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import CalendarHeatmap from './CalendarHeatmap';
import { dailyOccupancyDataConst } from 'src/dailyOccupancyDataConst';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import { COLOR_PRIMARY_2 } from 'src/colors';
import { User } from 'src/interfaces/iuser';
import { LoadingCard } from 'src/App/HOC';

interface YearMonth {
  year: number;
  month: number;
}

interface Props {
  user: User;
  marketDisplayName: string;
  cachedMarketData: any;
  parentMarketCacheData: any;
  parentMarketID: string | number | null;
  handleOnSampleClick: (plan: string) => void;
  isMobileScreen: boolean;
  isDemoMarket: boolean;
}

const DailyOccupancyRateCalendar: React.FC<Props> = ({
  user,
  marketDisplayName,
  cachedMarketData,
  parentMarketCacheData,
  parentMarketID,
  handleOnSampleClick,
  isMobileScreen,
  isDemoMarket,
}) => {
  const upgradeMessage = useUpgradeMesageExpertFeature();

  const { isLoading, occupancyData, hasRealData } = useMemo(() => {
    const cache = parentMarketID ? parentMarketCacheData : cachedMarketData;

    const loading = cache === 'loading' ? true : false;

    const data = cache?.data?.occupancy_breakdown?.daily_historical_data ?? dailyOccupancyDataConst;
    const hasRealData = cache?.data?.occupancy_breakdown?.daily_historical_data ? true : false;

    return { isLoading: loading, occupancyData: data, hasRealData };
  }, [parentMarketID, cachedMarketData, parentMarketCacheData]);

  const uniqueYearMonths: YearMonth[] = useMemo(() => {
    const seen = new Set<string>();
    const result: YearMonth[] = [];

    Object.keys(occupancyData).forEach(dateStr => {
      const [yearStr, monthStr] = dateStr.split('-');
      const year = +yearStr;
      const month = +monthStr - 1;

      const key = `${year}-${month}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ year, month });
      }
    });

    result.sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year));

    return result;
  }, [occupancyData]);


  const isNotPremiumUser = !['expert', 'scale'].includes(user.subscriptionPlan);

  const isDataBlur = !isLoading && (isNotPremiumUser || !hasRealData);

  return (
    <Grid
      container
      sx={{
        p: 2,
        boxShadow: '0 4px 10px #0003',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Typography variant="body1" className="subHeadings">
        Daily Occupancy Calendar of {marketDisplayName}
      </Typography>

      <Grid
        container
        justifyContent="center"
        alignItems="flex-start"
        flexWrap="wrap"
        sx={{
          gap: 4,
          mt: 2,
          filter: isDataBlur ? 'blur(4px) brightness(1.1)' : 'none',
          pointerEvents: isDataBlur ? 'none' : 'auto',
        }}
      >
        {isLoading && <LoadingCard height={300} />}

        {!isLoading &&
          uniqueYearMonths.map(({ year, month }) => (
            <Grid item key={`${year}-${month}`}>
              <CalendarHeatmap year={year} month={month} occupancyData={occupancyData} />
            </Grid>
          ))}
      </Grid>

      {isDataBlur && (
        <Typography
          variant="body1"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: isMobileScreen ? 18 : 22,
            fontWeight: 600,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            pointerEvents: 'auto',
            textDecoration: 'underline',
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: '#fffe',
          }}
          onClick={() => isNotPremiumUser && handleOnSampleClick('expert')}
        >
          {!isLoading && isNotPremiumUser
            ? upgradeMessage
            : 'Daily Occupancy Calendar is available exclusively in verified markets. Use the search bar to find verified markets, marked with a blue checkmark. (If the market is verified but the calendar is not showing yet, it is coming soon.)'}
        </Typography>
      )}
    </Grid>
  );
};

export default React.memo(DailyOccupancyRateCalendar);
